import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardMedia, 
  CardActionArea, 
  Grid, 
  Chip, 
  Divider,
  CircularProgress,
  Button
} from '@mui/material';
import { 
  CalendarToday as CalendarIcon,
  Person as PersonIcon
} from '@mui/icons-material';

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);

  useEffect(() => {
    // Simulate loading news from API
    const fetchNews = async () => {
      try {
        // For now, initialize with empty array
        // In the future, this would be replaced with:
        // const newsAPI = await import('../services/ApiService');
        // const newsData = await newsAPI.getAllNews();
        // setNews(newsData);
        
        setNews([]);
        setLoading(false);
      } catch (error) {
        console.error('Error loading news:', error);
        setNews([]);
        setLoading(false);
      }
    };
    
    fetchNews();
  }, []);
  
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  const getCategoryColor = (category) => {
    const categoryColors = {
      'Research': 'primary',
      'Technology': 'secondary',
      'Climate': 'error',
      'Market': 'success',
      'Policy': 'warning',
      'Event': 'info'
    };
    
    return categoryColors[category] || 'default';
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
    window.scrollTo(0, 0);
  };

  const handleBackToList = () => {
    setSelectedArticle(null);
  };

  const renderArticleList = () => (
    <Grid container spacing={3}>
      {news.map((item) => (
        <Grid item xs={12} key={item.id}>
          <Card sx={{ display: { sm: 'flex' }, mb: 2 }}>
            <CardMedia
              component="img"
              sx={{ 
                width: { xs: '100%', sm: 200 },
                height: { xs: 200, sm: 'auto' }
              }}
              image={item.image}
              alt={item.title}
            />
            <CardContent sx={{ flex: '1 1 auto' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" component="div">
                  {item.title}
                </Typography>
                <Chip 
                  label={item.category} 
                  color={getCategoryColor(item.category)}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Box>
              
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {formatDate(item.date)}
              </Typography>
              
              <Divider sx={{ my: 1 }} />
              
              <Typography variant="body2" color="text.secondary" paragraph>
                {item.content.length > 200 
                  ? `${item.content.substring(0, 200)}...` 
                  : item.content
                }
              </Typography>
              
              <Button size="small" color="primary" onClick={() => handleArticleClick(item)}>
                Read More
              </Button>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderArticleDetail = () => (
    <Box>
      <Button 
        variant="outlined" 
        onClick={handleBackToList} 
        sx={{ mb: 2 }}
      >
        Back to News List
      </Button>
      
      <Card elevation={3}>
        <CardMedia
          component="img"
          height="300"
          image={selectedArticle.image}
          alt={selectedArticle.title}
        />
        <CardContent>
          <Typography variant="h5" component="div" gutterBottom>
            {selectedArticle.title}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon fontSize="small" sx={{ mr: 0.5 }} />
              {selectedArticle.author}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarIcon fontSize="small" sx={{ mr: 0.5 }} />
              {new Date(selectedArticle.date).toLocaleDateString()}
            </Typography>
          </Box>
          
          <Chip 
            label={selectedArticle.category} 
            size="small" 
            color={getCategoryColor(selectedArticle.category)} 
            sx={{ mb: 2 }} 
          />
          
          <Divider sx={{ mb: 2 }} />
          
          <Typography variant="body1" paragraph>
            {selectedArticle.content.split('\n\n').map((paragraph, index) => (
              <Typography key={index} variant="body1" paragraph>
                {paragraph}
              </Typography>
            ))}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Box sx={{ p: 2, mb: 8 }}>
      <Typography variant="h5" gutterBottom>
        Industry News
      </Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : selectedArticle ? (
        renderArticleDetail()
      ) : (
        renderArticleList()
      )}
    </Box>
  );
};

export default NewsPage; 